import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, Megaphone, Calendar, X, CreditCard } from "lucide-react";
import { StudentSection } from "./StudentLayout";
import { api } from "@/lib/api";
import { todayISO } from "@/components/DateNav";

const BELT_CLASS = {
  white: "belt-white", blue: "belt-blue", purple: "belt-purple", brown: "belt-brown", black: "belt-black",
  "gray-white": "belt-gray-white", gray: "belt-gray", "gray-black": "belt-gray-black",
  "yellow-white": "belt-yellow-white", yellow: "belt-yellow", "yellow-black": "belt-yellow-black",
  "orange-white": "belt-orange-white", orange: "belt-orange", "orange-black": "belt-orange-black",
  "green-white": "belt-green-white", green: "belt-green", "green-black": "belt-green-black",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso); const diff = (Date.now() - d.getTime())/1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff/60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  if (diff < 86400*2) return "Yesterday";
  return `${Math.floor(diff/86400)}d ago`;
}

export default function StudentHome() {
  const [me, setMe] = useState(null);
  const [today, setToday] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [anns, setAnns] = useState([]);
  const [billing, setBilling] = useState(null);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    const [meR, todayR, resR, annsR, billingR] = await Promise.all([
      api.get("/student/me"),
      api.get("/student/classes/today"),
      api.get("/student/reservations"),
      api.get("/student/announcements"),
      api.get("/billing/me").catch(() => ({ data: null })),
    ]);
    setMe(meR.data); setToday(todayR.data); setReservations(resR.data); setAnns(annsR.data); setBilling(billingR.data);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!me) return <div className="p-8 label">Loading…</div>;
  const s = me.student;

  const reserve = async (klass) => {
    await api.post("/student/reserve", { class_id: klass.class_id, date: klass.date });
    refresh();
  };
  const cancel = async (klass) => {
    await api.post("/student/cancel", { class_id: klass.class_id, date: klass.date });
    refresh();
  };

  const greeting = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; })();

  const avatarSrc = s.avatar || me.user?.picture;

  return (
    <div data-testid="page-student-home">
      <StudentSection section={greeting.toUpperCase()} title={(s.name || "Athlete").split(" ")[0]} />

      {/* Streak banner */}
      <div className="px-5">
        <div className="card p-5 flex items-center gap-4" data-testid="streak-banner" style={{ background: "linear-gradient(135deg, rgba(61,220,126,0.12), rgba(61,220,126,0.02))", border: "1px solid var(--green-border)" }}>
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" style={{ border: "2px solid var(--green-border)" }} data-testid="home-avatar"/>
          ) : (
            <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--green)" }}>
              <Flame size={22} color="#062012"/>
            </div>
          )}
          <div className="flex-1">
            <div className="text-[26px] font-bold tracking-tight">{s.streak || 0}<span className="text-[15px] mono ml-1" style={{color:"var(--text-dim)"}}>day streak</span></div>
            <div className="label-tiny mt-1">{me.last30_classes} classes in last 30 days · keep it rolling</div>
          </div>
          <span className={`belt-bar ${BELT_CLASS[s.belt] || "belt-white"}`} style={{ width: 36, height: 10 }}/>
        </div>
      </div>

      {/* Quick stats */}
      <div className="px-5 mt-4 grid grid-cols-3 gap-3">
        <div className="card p-3 text-center" data-testid="stat-belt"><div className="label-tiny">Belt</div><div className="text-[18px] font-bold mt-1 uppercase">{s.belt}</div><div className="mono text-[11px]" style={{color:"var(--text-dim)"}}>{s.stripes} stripes</div></div>
        <div className="card p-3 text-center" data-testid="stat-classes"><div className="label-tiny">Classes</div><div className="text-[18px] font-bold mt-1">{s.classes_count || 0}</div><div className="mono text-[11px]" style={{color:"var(--text-dim)"}}>total</div></div>
        <div className="card p-3 text-center" data-testid="stat-status"><div className="label-tiny">Status</div><div className="text-[15px] font-bold mt-1 uppercase">{s.status}</div><div className="mono text-[11px]" style={{color:"var(--text-dim)"}}>{s.months} mo</div></div>
      </div>

      {/* Membership status — only surface prominently when not-active or approaching due */}
      {billing && (billing.status !== "active" || (billing.days_until_due !== null && billing.days_until_due <= 5)) && (
        <div className="px-5 mt-4">
          <button onClick={() => nav("/student/billing")} data-testid="home-billing-card"
                  className="card p-4 w-full flex items-center gap-3 text-left"
                  style={{
                    borderColor: billing.status === "past_due" ? "rgba(255,80,80,0.4)"
                      : billing.status === "active" && billing.days_until_due <= 3 ? "rgba(244,185,66,0.4)"
                      : "var(--border)",
                    background: billing.status === "past_due" ? "rgba(255,80,80,0.06)"
                      : billing.status === "active" && billing.days_until_due <= 3 ? "rgba(244,185,66,0.06)"
                      : undefined,
                  }}>
            <CreditCard size={22} style={{
              color: billing.status === "past_due" ? "rgb(255,120,120)"
                : billing.status === "active" ? "var(--green)" : "var(--text-dim)",
            }}/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13px] uppercase tracking-tight">
                {billing.status === "past_due" ? "Payment failed"
                  : billing.status === "active" && billing.days_until_due === 0 ? "Renews today"
                  : billing.status === "active" ? `Renews in ${billing.days_until_due} day${billing.days_until_due === 1 ? "" : "s"}`
                  : "Start your membership"}
              </div>
              <div className="label-tiny mt-0.5 truncate">
                {billing.status === "past_due" ? `Update your card to keep training`
                  : billing.status === "active" ? `$${billing.amount}/mo · tap to manage`
                  : `$${billing.amount}/mo · tap to subscribe`}
              </div>
            </div>
          </button>
        </div>
      )}

      {/* My upcoming reservations */}
      {reservations.length > 0 && (
        <div className="px-5 mt-6" data-testid="upcoming-reservations">
          <div className="flex items-end justify-between mb-3">
            <div className="label">My reservations</div>
            <div className="label-tiny">{reservations.length} upcoming</div>
          </div>
          <div className="space-y-2">
            {reservations.slice(0, 5).map(r => {
              const isToday = r.is_today ?? (r.date === todayISO());
              return (
                <div key={r.reservation_id}
                     className="card p-4 flex items-center gap-3"
                     data-testid={`reservation-${r.reservation_id}`}
                     style={{ borderColor: isToday ? "var(--green-border)" : "var(--border)",
                              background: isToday ? "linear-gradient(135deg, rgba(61,220,126,0.08), rgba(61,220,126,0.01))" : undefined }}>
                  <div className="text-center shrink-0" style={{ width: 44 }}>
                    <div className="label-tiny" style={{ color: isToday ? "var(--green)" : "var(--text-dim)", fontWeight: 700 }}>
                      {isToday ? "TODAY" : r.day_label.toUpperCase()}
                    </div>
                    <div className="mono text-[12px] mt-0.5" style={{ color: "var(--text-dim)" }}>{r.date_label}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold uppercase tracking-tight text-[14px] truncate">{r.name}</div>
                    <div className="label-tiny mt-0.5">
                      <span className="mono">{r.start_time}</span> · {r.instructor || "Coach"} · {r.mat}
                    </div>
                    <div className="label-tiny mt-0.5" style={{ color: "var(--text-muted)" }}>
                      {r.reserved_count}/{r.capacity} spots · {r.duration_min || 60} min
                    </div>
                  </div>
                  <button onClick={() => cancel(r)}
                          className="pill shrink-0"
                          style={{ background: "transparent", color: "var(--text-dim)", border: "1px solid var(--border)" }}
                          data-testid={`cancel-res-${r.reservation_id}`}
                          title="Cancel reservation">
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Today's classes */}
      <div className="px-5 mt-6">
        <div className="flex items-end justify-between mb-3">
          <div className="label">Today's classes</div>
          <button className="label-tiny hover:text-white" onClick={()=>nav("/student/classes")} data-testid="link-all-classes">View all →</button>
        </div>
        {today.length === 0 && <div className="card p-6 text-center"><Calendar size={22} style={{color:"var(--text-muted)", margin:"0 auto"}}/><div className="label-tiny mt-3">No classes today. Enjoy recovery.</div></div>}
        <div className="space-y-2">
          {today.map(c => (
            <div key={c.class_id} className="card p-4 flex items-center gap-4" data-testid={`today-class-${c.class_id}`}>
              <div className="mono text-[13px]" style={{width:46, color:"var(--text-dim)"}}>{c.start_time}</div>
              <div className="flex-1">
                <div className="font-semibold uppercase tracking-tight text-[14px]">{c.name}</div>
                <div className="label-tiny mt-0.5">{c.instructor} · {c.reserved_count}/{c.capacity}</div>
              </div>
              {c.reserved ? (
                <button onClick={()=>cancel(c)} className="pill pill-active" data-testid={`cancel-${c.class_id}`}>Reserved</button>
              ) : (
                <button onClick={()=>reserve(c)} className="pill" style={{background:"transparent", color:"var(--green)", border:"1px solid var(--green-border)"}} data-testid={`reserve-${c.class_id}`}>Reserve</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Announcements */}
      <div className="px-5 mt-6">
        <div className="label mb-3">From the academy</div>
        <div className="space-y-2">
          {anns.slice(0,3).map(a => (
            <div key={a.ann_id} className="card p-4" data-testid={`ann-${a.ann_id}`}>
              <div className="flex items-start gap-2">
                <Megaphone size={14} style={{color:"var(--green)", marginTop: 3}}/>
                <div className="flex-1">
                  <div className="font-semibold uppercase tracking-tight text-[13px]">{a.title}</div>
                  <div className="label-tiny mt-1 leading-relaxed" style={{textTransform:"none", letterSpacing:0, fontSize:12, color:"var(--text-dim)"}}>{a.message}</div>
                  <div className="label-tiny mt-2">{timeAgo(a.created_at)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
